import { useState } from 'react';

import { toast } from 'react-toastify';

import { useWalletPolicies, type IWalletPolicy } from '@entities/wallets';
import { useUpdateWalletPolicyMutation } from '@entities/wallets/api';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Paper, Typography, IconButton, Button, Divider, Menu, MenuItem, Box } from '@mui/material';

interface IProps {
  selectedCount: number;
  onClear?: () => void;
  selectedIds?: string[];
}

export const BulkActions = ({ selectedCount, onClear, selectedIds = [] }: IProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingSelection, setPendingSelection] = useState<string | null>(null);
  const [pendingTraffic, setPendingTraffic] = useState<string | null>(null);
  const [updateWalletPolicy, { isLoading }] = useUpdateWalletPolicyMutation();

  const { walletPolicies, isLoading: isLoadingWalletPolicies } = useWalletPolicies();
  if (!selectedCount || selectedCount <= 0 || isLoadingWalletPolicies) return null;

  const open = Boolean(anchorEl);
  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (open) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
    setPendingSelection(null);
    setPendingTraffic(null);
  };

  const handleSelect = (option: IWalletPolicy) => {
    setPendingSelection(option.wallet_policy.name);
    setPendingTraffic(option.traffic);
  };

  const handleCancel = () => {
    setPendingSelection(null);
    setPendingTraffic(null);
    handleClose();
  };

  const handleApply = async () => {
    if (!pendingSelection) return;
    try {
      await updateWalletPolicy({
        parent_wallet_ids: selectedIds,
        policy_name: pendingSelection,
        traffic: pendingTraffic,
      }).unwrap();
      toast.success('Bulk change wallet policy successfully');
      handleClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Mock bulk update failed', e);
    }
  };

  const itemsLabel = `${selectedCount} ${selectedCount === 1 ? 'Item' : 'Items'} selected`;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 16,
        zIndex: theme => theme.zIndex.snackbar + 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        borderRadius: 2,
        width: 'fit-content',
        maxWidth: 'calc(100% - 32px)',
      }}
    >
      <IconButton aria-label='clear selection' onClick={onClear} disabled={!onClear}>
        <CloseIcon />
      </IconButton>

      <Typography variant='body1' sx={{ fontWeight: 500 }}>
        {itemsLabel}
      </Typography>

      <Divider orientation='vertical' flexItem />

      <Button
        id='bulk-change-wallet-type'
        variant='text'
        endIcon={<KeyboardArrowUpIcon />}
        onClick={handleToggle}
      >
        Change wallet policy
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          list: { 'aria-labelledby': 'bulk-change-wallet-type' },
          paper: { sx: { minWidth: 280, p: 1 } },
        }}
      >
        {walletPolicies.data.map(policy => (
          <MenuItem
            key={policy.wallet_policy.name}
            selected={pendingSelection === policy.wallet_policy.name}
            onClick={() => handleSelect(policy)}
          >
            {policy.wallet_policy.name}
          </MenuItem>
        ))}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'space-between', px: 1 }}>
          <Button onClick={handleCancel} variant='outlined' color='error'>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant='contained'
            disabled={!pendingSelection || isLoading || selectedIds.length === 0}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </Paper>
  );
};

export default BulkActions;
